import { useState, useEffect, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faRotate, faSpinner, faFileLines } from '@fortawesome/free-solid-svg-icons';
import { dockerService } from '../services/dockerService';

/**
 * ContainerLogs Component
 * Displays container logs with auto-scroll
 */
export function ContainerLogs({ containerId }) {
  const [logs, setLogs] = useState('');
  const [loading, setLoading] = useState(true);
  const [tail, setTail] = useState(100);
  const logsEndRef = useRef(null);

  const fetchLogs = async () => {
    try {
      setLoading(true);
      const data = await dockerService.getContainerLogs(containerId, tail);
      setLogs(data.logs || 'No logs available');
    } catch (error) {
      console.error('Error fetching logs:', error);
      setLogs(`Error: ${error.message}`);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLogs();
  }, [containerId, tail]);

  // Auto-scroll to bottom
  useEffect(() => {
    logsEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [logs]);

  if (loading && !logs) {
    return (
      <div className="flex items-center justify-center h-64">
        <FontAwesomeIcon icon={faSpinner} spin size="2x" className="text-theme-button-color" />
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Controls */}
      <div className="bg-theme-secondary-bg-color border-b border-theme-section-separator-color p-3 sm:p-4 flex flex-col sm:flex-row items-stretch sm:items-center justify-between gap-2 sm:gap-0">
        <div className="flex items-center gap-2">
          <label className="text-theme-text-color text-sm">Lines:</label>
          <select
            value={tail}
            onChange={(e) => setTail(Number(e.target.value))}
            className="bg-theme-bg-color text-theme-text-color px-3 py-2 rounded-lg border border-theme-section-separator-color text-sm"
          >
            <option value="50">50</option>
            <option value="100">100</option>
            <option value="200">200</option>
            <option value="500">500</option>
            <option value="1000">1000</option>
          </select>
        </div>
        <button
          onClick={fetchLogs}
          disabled={loading}
          className="px-4 py-2 rounded-lg bg-theme-button-color text-theme-button-text-color disabled:opacity-50 flex items-center justify-center gap-2 text-sm sm:text-base"
        >
          <FontAwesomeIcon icon={faRotate} className={loading ? 'animate-spin' : ''} />
          <span>Refresh</span>
        </button>
      </div>

      {/* Logs */}
      <div className="flex-1 overflow-y-auto bg-black p-2 sm:p-4 font-mono text-xs">
        {logs ? (
          <pre className="text-green-400 whitespace-pre-wrap break-words">{logs}</pre>
        ) : (
          <div className="flex flex-col items-center justify-center h-full text-gray-500">
            <FontAwesomeIcon icon={faFileLines} size="2x" className="opacity-40 mb-4" />
            <p>No logs available</p>
          </div>
        )}
        <div ref={logsEndRef} />
      </div>
    </div>
  );
}

export default ContainerLogs;
